import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import Pagination from '@features/ui/components/pagination';
import { useNavigate } from 'react-router-dom';
import { getUserDemographicsReport } from '@src/services/reports';
import { toast } from 'react-toastify';
import { TReportUserDemographicsData } from '@src/types';

const pageSize = 25;

export const UserDemographicsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState<TReportUserDemographicsData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  const handleRunReport = async () => {
    try {
      const response = await getUserDemographicsReport({
        group_by: 'country',
        page: currentPage,
        size: pageSize,
      });

      setData(response.data);
      setTotalRecords(response.total);
      setTotalPages(response.pages);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch data');
    }
  };

  const handleCountryClick = useCallback(
    (location: string) => {
      navigate(`/admin/browse-users?country=${encodeURIComponent(location)}`);
    },
    [navigate]
  );

  const columns: TColumn<TReportUserDemographicsData>[] = useMemo(
    () => [
      {
        title: 'COUNTRY',
        render: (row) => <p className="text-sm">{row?.location || '-'}</p>,
      },
      {
        title: 'NUMBER OF USERS',
        className: 'text-right',
        render: (row) => (
          <Button
            className="text-[#3490FF] underline w-fit ml-auto pl-0 text-sm font-normal"
            variant="link"
            onClick={() =>
              row?.location && handleCountryClick(row.location_code)
            }
          >
            {row?.user_count?.toLocaleString() || '0'}
          </Button>
        ),
      },
    ],
    [handleCountryClick]
  );

  const recordsInfo = useMemo(() => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalRecords);

    return `${start}-${end}`;
  }, [currentPage, totalRecords]);

  useEffect(() => {
    handleRunReport();
  }, [currentPage]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">User Demographics</h1>
        <p className="text-sm text-gray-400">
          showing records {recordsInfo} from {totalRecords.toLocaleString()}
        </p>
      </div>

      <Card className="p-6">
        <RadixTable columns={columns} data={data} />

        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              current={currentPage}
              total={totalPages}
              onChange={setCurrentPage}
            />
          </div>
        )}
      </Card>
    </div>
  );
};
